import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssignDailyTruckPlannedMutation } from '../../../../hooks/mutations/useDailyTrucksPlannedMutation';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { DailyTruckPlanned } from '../../../../services/apiSLTP/dailyReports/dailyTrucksPlanned';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { isSameDayOrAfter } from '../../../../utils/dates';
import { permissionLevels, reportTypeEnum } from '../../../../utils/enums';
import DropDown, { OptionType } from '../../../DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';
import { DataItem } from '../../../../services/apiSLTP/activities';
import { useReportsQuery } from '../../../../hooks/queries/useReportsQuery';

interface AssignTruckPlannedModalProps extends ModalProps {
    refetch?: () => void;
    dailyTruckPlanned: DailyTruckPlanned;
    activities: DataItem[];
    currentDate: Date;
}

const AssignTruckPlannedModal: React.FC<AssignTruckPlannedModalProps> = ({
    open,
    handleClose,
    refetch = () => {},
    dailyTruckPlanned,
    
    activities,
    currentDate,
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });

    const { mutate: assignRide, isLoading: isAssignLoading } = useAssignDailyTruckPlannedMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const {
        data: dataReports,
        isLoading: isReportsLoading,
        refetch: refetchReports,
    } = useReportsQuery({ queries: { date: currentDate.toISOString() } });

    const [selectedUsers, setSelectedUsers] = useState<OptionType[] | null>(Array(dailyTruckPlanned.trucksAmount).fill({ label: '', value: '' }));

    const dropdownItems =
        dataUsers
            ?.filter(
                (user) =>
                    user.profileId.permsLevel === permissionLevels.CHAUFFEUR &&
                    !user.isSpecial &&
                    !dataReports
                        ?.filter((report) => report.reportType === reportTypeEnum.ABSENT)
                        .map((report) => report.userId._id)
                        .includes(user._id)
            )
            ?.map((user) => ({
                label: formatName(user.firstName, user.lastName),
                value: user._id,
                group: activities
                    .filter((activity) => activity.reportId.userId._id === user._id)
                    .some((activity) => {
                        return activity.dailyTrucksPlanned.length > 0;
                    })
                    ? capitalizeFirstLetter(t('alreadyAssigned'))
                    : capitalizeFirstLetter(t('remaining')),
            })) || [];

    const handleUserChange = (index: number, value: OptionType) => {
        const newSelected = selectedUsers ? [...selectedUsers] : [];
        newSelected[index] = value;
        setSelectedUsers(newSelected);
    };

    return (
        <ModalBody
            closeText={t('cancel')}
            okText={t('validate')}
            title={t('activity')}
            subTitle={t('assignRide')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                assignRide({
                    dailyTruckPlannedId: dailyTruckPlanned._id,
                    userIds: selectedUsers?.map((u) => u.value).filter((u) => u.length) || [],
                });
            }}
            
            loading={isAssignLoading}
        >
            {selectedUsers?.map((selectedUser, index) => (
                <DropDown
                    key={index}
                    selectedItem={selectedUser}
                    items={dropdownItems
                        .sort((a, b) => {
                            const compare = capitalizeFirstLetter(t('remaining'));
                            if (a.group === compare) return -1;
                            if (b.group === compare) return -1;
                            return 0;
                        })
                        .filter((user) => selectedUser.value === user.value || !selectedUsers.map((u) => u.value).includes(user.value))}
                    handleChange={(event, value) => handleUserChange(index, { value: value?.value || '', label: value?.label || '' })}
                    inputLabel={`${t('person')} ${index + 1}`}
                    loading={isUsersLoading}
                    grouped
                    sortGroups={false}
                />
            ))}
        </ModalBody>
    );
};

export default AssignTruckPlannedModal;
